import { IRequesterError } from '@/services/RequestService/IRequester';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

export type TQueryOptions<T = unknown, E = Error, D = T> = Omit<UseQueryOptions<T, E, D>, 'queryKey' | 'queryFn'>;
export type TInfiniteQueryOptions<T = unknown, E = Error, D = T> = Omit<
  UseInfiniteQueryOptions<T, E, D, T, QueryKey>,
  'queryKey' | 'queryFn'
>;

/*
 * useSunnyMutation 의 options type 정의
 *
 * @template T - 성공 시 반환될 데이터의 타입
 * @template E - 에러 시 반환될 에러 객체의 타입
 * @template V - 뮤테이션에 전달할 객체의 타입
 * @template C - onMutate callback 함수의 return값을 지정하는 타입 (onMutate의 결과 값을 활용하려면 해당 타입을 지정해서 활용)
 */
export interface ISunnyMutationOptions<T = unknown, E = IRequesterError, V = unknown, C = unknown>
  extends Omit<UseMutationOptions<T, E, V, C>, 'mutationFn'> {}

/**
 * 공통 useQuery
 *
 * - window 포커스 시 재호출 X
 * - retry X
 */
function useSunnyQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  queryOptions: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> = {},
): UseQueryResult<TData, TError> {
  return useQuery(queryKey, queryFn, {
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions,
  });
}

/**
 * 캐시 추가된 공통 useQuery
 *
 * - 5분(staleTime) 동안 캐시 데이터 사용
 * - 서버 사이드에서 dehydration한 경우 클라이언트에서 동일 queryKey는 중복 호출 안함
 * - window 포커스 시 재호출 X
 * - retry X
 */
export function useSunnyCacheQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  queryOptions: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> = {},
): UseQueryResult<TData, TError> {
  return useSunnyQuery(queryKey, queryFn, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...queryOptions,
  });
}

/**
 * 공통 useMutation
 *
 * - retry 1회
 */
export function useSunnyMutation<TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationKey'>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  return useMutation(mutationFn, {
    retry: 1,
    ...options,
  });
}

/**
 * 공통 useInfiniteQuery
 *
 * - 5분(staleTime) 동안 캐시 데이터 사용
 * - 서버 사이드에서 dehydration한 경우 클라이언트에서 동일 queryKey는 중복 호출 안함
 * - window 포커스 시 재호출 X
 * - retry X
 */
export function useSunnyCacheInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  queryOptions: Omit<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>,
    'queryKey' | 'queryFn'
  > = {},
): UseInfiniteQueryResult<TData, TError> {
  return useInfiniteQuery(queryKey, queryFn, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions,
  });
}

export default useSunnyQuery;
